const API_BASE = '/api';

interface RequestOptions extends RequestInit {
  params?: Record<string, string | number | boolean | undefined>;
  /** Internal flag to prevent infinite CSRF retry loops */
  _csrfRetried?: boolean;
  /**
   * Non-2xx HTTP statuses to surface as parsed JSON instead of throwing.
   * Used by the rewrite push endpoint to flow 409 (draft_exists) through to
   * the UI without coercing into an Error.
   */
  passthroughStatuses?: number[];
}

export interface DownloadResult {
  blob: Blob;
  fileName: string | null;
}

function getFileNameFromContentDisposition(header: string | null): string | null {
  if (!header) {
    return null;
  }

  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }

  const quotedMatch = header.match(/filename="([^"]+)"/i);
  if (quotedMatch?.[1]) {
    return quotedMatch[1];
  }

  const plainMatch = header.match(/filename=([^;]+)/i);
  return plainMatch?.[1]?.trim() ?? null;
}

class ApiClient {
  private csrfToken: string | null = null;

  setCsrfToken(token: string | null) {
    this.csrfToken = token;
  }

  getCsrfToken(): string | null {
    return this.csrfToken;
  }

  async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const { params, ...fetchOptions } = options;

    let url = `${API_BASE}${endpoint}`;

    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.set(key, String(value));
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    // Add CSRF token for state-changing requests
    const method = (fetchOptions.method ?? 'GET').toUpperCase();
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method) && this.csrfToken) {
      headers['X-CSRF-Token'] = this.csrfToken;
    }

    const response = await fetch(url, {
      ...fetchOptions,
      headers,
      // Include cookies in all requests
      credentials: 'include',
      signal: fetchOptions.signal ?? AbortSignal.timeout(120_000),
    });

    if (response.status === 401) {
      // Don't try to refresh for auth-checking endpoints to avoid infinite loops
      const isAuthCheckEndpoint = endpoint === '/auth/me' || endpoint === '/auth/csrf-token';

      if (!isAuthCheckEndpoint) {
        // Try to refresh the session
        const refreshed = await this.tryRefresh();
        if (refreshed) {
          // Retry the original request
          return this.request<T>(endpoint, options);
        }
      }

      // Let the calling code handle the 401 - don't force a redirect here
      throw new Error('Unauthorized');
    }

    // CSRF token mismatch — re-fetch the token and retry once
    if (response.status === 403 && !options._csrfRetried) {
      const cloned = response.clone();
      const body = await cloned.json().catch(() => null);
      const msg = body?.error?.message ?? body?.message ?? '';
      if (typeof msg === 'string' && msg.toLowerCase().includes('csrf')) {
        const refreshed = await this.refreshCsrfToken();
        if (refreshed) {
          return this.request<T>(endpoint, { ...options, _csrfRetried: true } as RequestOptions);
        }
      }
    }

    if (!response.ok) {
      if (options.passthroughStatuses?.includes(response.status)) {
        return response.json();
      }
      const body = await response.json().catch(() => null);
      const message =
        (typeof body?.error === 'string' && body.error) ||
        body?.error?.message ||
        body?.message ||
        `Request failed (${response.status})`;
      throw new Error(message);
    }

    return response.json();
  }

  private async refreshCsrfToken(): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE}/auth/csrf-token`, {
        credentials: 'include',
      });
      if (!response.ok) return false;
      const data = await response.json();
      if (data.csrfToken) {
        this.csrfToken = data.csrfToken;
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  private async tryRefresh(): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        return false;
      }

      return true;
    } catch {
      return false;
    }
  }

  get<T>(endpoint: string, options?: RequestOptions) {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  post<T>(endpoint: string, data?: unknown, options?: RequestOptions) {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  patch<T>(endpoint: string, data?: unknown, options?: RequestOptions) {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  delete<T>(endpoint: string, options?: RequestOptions) {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }

  async download(endpoint: string, options: RequestOptions = {}): Promise<DownloadResult> {
    const { params, ...fetchOptions } = options;

    let url = `${API_BASE}${endpoint}`;

    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.set(key, String(value));
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string>),
    };

    const method = (fetchOptions.method ?? 'GET').toUpperCase();
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method) && this.csrfToken) {
      headers['X-CSRF-Token'] = this.csrfToken;
    }

    const response = await fetch(url, {
      ...fetchOptions,
      headers,
      credentials: 'include',
      signal: fetchOptions.signal ?? AbortSignal.timeout(120_000),
    });

    if (response.status === 401) {
      const isAuthCheckEndpoint = endpoint === '/auth/me' || endpoint === '/auth/csrf-token';

      if (!isAuthCheckEndpoint) {
        const refreshed = await this.tryRefresh();
        if (refreshed) {
          return this.download(endpoint, options);
        }
      }

      throw new Error('Unauthorized');
    }

    if (response.status === 403 && !options._csrfRetried) {
      const cloned = response.clone();
      const body = await cloned.json().catch(() => null);
      const msg = body?.error?.message ?? body?.message ?? '';
      if (typeof msg === 'string' && msg.toLowerCase().includes('csrf')) {
        const refreshed = await this.refreshCsrfToken();
        if (refreshed) {
          return this.download(endpoint, { ...options, _csrfRetried: true } as RequestOptions);
        }
      }
    }

    if (!response.ok) {
      const contentType = response.headers.get('content-type') ?? '';
      let message = `Request failed (${response.status})`;

      if (contentType.includes('application/json')) {
        const body = await response.json().catch(() => null);
        message =
          (typeof body?.error === 'string' && body.error) ||
          body?.error?.message ||
          body?.message ||
          message;
      } else {
        const text = await response.text().catch(() => '');
        if (text) {
          message = text;
        }
      }

      throw new Error(message);
    }

    return {
      blob: await response.blob(),
      fileName: getFileNameFromContentDisposition(response.headers.get('Content-Disposition')),
    };
  }
}

export const apiClient = new ApiClient();
